/**
 * Helpers for "listing collections": curated or computed groups of listings that are
 * rendered as a carousel (e.g. "More from this seller", "Similar listings", "Just listed").
 *
 * Every collection is described by a plain config object (see config/configListingCollections.js)
 * and turned into a single Marketplace API listing query by buildCollectionQueryParams.
 * Keeping this layer pure means all four collection types share one fetch path and one component.
 */
import { createImageVariantConfig } from './sdkLoader';
import { FEED_IMAGE_LIMIT } from './listingImages';

// Where the listings of a collection come from.
// - QUERY:   static query params (sort, meta_*, pub_*, ids, ...). Operator-curated collections.
// - AUTHOR:  other listings from the author of the listing currently on screen.
// - SIMILAR: listings that share the deepest category (or listing type) with the current listing.
// - TAG:     listings an operator tagged with metadata.collections. Deliberately a different
//            key from metadata.badges, so curating a listing never puts a pill on its card.
export const COLLECTION_SOURCES = {
  QUERY: 'query',
  AUTHOR: 'author',
  SIMILAR: 'similar',
  TAG: 'tag',
};

// Marketplace API caps perPage at 100.
const MAX_PER_PAGE = 100;
const DEFAULT_LIMIT = 8;

// The section type used when a collection is rendered through the PageBuilder.
export const LISTING_COLLECTION_SECTION_TYPE = 'listingCollection';

/**
 * Image and include params shared by every collection query.
 * Mirrors what ProfilePage/SearchPage request, so ListingCard has everything it needs.
 *
 * @param {Object} config marketplace configuration
 * @returns {Object} sdk.listings.query params
 */
export const listingQueryImageParams = config => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    include: ['author', 'images'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-300`,
      `variants.${variantPrefix}-600`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-300`, 300, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-600`, 600, aspectRatio),
    'limit.images': FEED_IMAGE_LIMIT,
  };
};

/**
 * Author id of a listing. Works with both denormalised entities (author.id) and
 * raw API entities from a listings/show response (relationships.author.data.id).
 *
 * @param {Object} listing
 * @returns {string|null} uuid string
 */
export const getAuthorId = listing => {
  const denormalised = listing?.author?.id?.uuid;
  const raw = listing?.relationships?.author?.data?.id?.uuid;
  return denormalised || raw || null;
};

// Match on the *top* category level, then rank the results by how deeply they match
// (see rankBySimilarity). Matching on the deepest level instead would be more precise but
// returns almost nothing: leaf categories typically hold one or two listings each, so the
// carousel would be empty on most listings. One query, widest pool, best matches first.
// Falls back to listing type when no categories are configured (both keys are indexed by default).
const similarityParams = (listing, config) => {
  const publicData = listing?.attributes?.publicData || {};
  const categoryLevelKeys = config?.categoryConfiguration?.categoryLevelKeys || [];

  const topLevelKey = categoryLevelKeys.find(key => !!publicData[key]);
  if (topLevelKey) {
    return { [`pub_${topLevelKey}`]: publicData[topLevelKey] };
  }

  const { listingType } = publicData;
  return listingType ? { pub_listingType: listingType } : null;
};

// How many nested category levels a listing shares with the reference listing.
// Levels are nested, so a mismatch at one level makes the deeper ones meaningless.
const categoryMatchDepth = (listing, referenceListing, categoryLevelKeys) => {
  const publicData = listing?.attributes?.publicData || {};
  const referenceData = referenceListing?.attributes?.publicData || {};

  let depth = 0;
  for (const key of categoryLevelKeys) {
    if (!referenceData[key] || publicData[key] !== referenceData[key]) {
      break;
    }
    depth += 1;
  }
  return depth;
};

/**
 * Order listings so the closest category matches come first (a listing in the same leaf
 * category before one that only shares the top level). Stable: equally close listings keep
 * the order the API returned them in.
 *
 * @param {Array} listings listings from the API
 * @param {Object} referenceListing listing being compared against
 * @param {Object} config marketplace configuration
 * @returns {Array} listings
 */
export const rankBySimilarity = (listings = [], referenceListing, config) => {
  const categoryLevelKeys = config?.categoryConfiguration?.categoryLevelKeys || [];
  if (categoryLevelKeys.length === 0) {
    return listings;
  }

  return listings
    .map((listing, index) => ({
      listing,
      index,
      depth: categoryMatchDepth(listing, referenceListing, categoryLevelKeys),
    }))
    .sort((a, b) => b.depth - a.depth || a.index - b.index)
    .map(entry => entry.listing);
};

const sourceParams = (collection, listing, config) => {
  switch (collection.source) {
    case COLLECTION_SOURCES.AUTHOR: {
      const authorId = getAuthorId(listing);
      return authorId ? { author_id: authorId } : null;
    }
    case COLLECTION_SOURCES.SIMILAR:
      return similarityParams(listing, config);
    case COLLECTION_SOURCES.TAG:
      return collection.tagSlug ? { meta_collections: collection.tagSlug } : null;
    case COLLECTION_SOURCES.QUERY:
    default:
      return {};
  }
};

/**
 * Turn a collection config into sdk.listings.query params.
 *
 * @param {Object} collection collection config (id, source, params, limit)
 * @param {Object} options
 * @param {Object} options.config marketplace configuration
 * @param {Object} [options.listing] listing in context (required for author/similar sources)
 * @param {string} [options.excludeListingId] uuid filtered out of the results client-side
 * @returns {Object|null} query params, or null when the collection can't be resolved
 */
export const buildCollectionQueryParams = (collection, options) => {
  const { config, listing, excludeListingId } = options || {};
  const resolvedSourceParams = sourceParams(collection, listing, config);

  // A collection that has nothing to filter on shouldn't fall back to "all listings".
  if (!resolvedSourceParams) {
    return null;
  }

  const limit = collection.limit || DEFAULT_LIMIT;
  // Over-fetch by one so the current listing can be dropped without leaving a gap:
  // the API has no "not this id" filter.
  const perPage = Math.min(limit + (excludeListingId ? 1 : 0), MAX_PER_PAGE);

  return {
    ...listingQueryImageParams(config),
    ...resolvedSourceParams,
    ...(collection.params || {}),
    perPage,
  };
};

/**
 * Drop listings that shouldn't be shown in a collection and truncate to the limit.
 *
 * excludeListingIds is how two collections on the same page avoid showing the same listing
 * twice (a seller's other listing is often also a similar listing). Only the query itself
 * reserves an extra slot, so a heavily deduplicated collection can end up shorter than its limit.
 *
 * @param {Array} listings denormalised listings
 * @param {Object} options
 * @param {string} [options.excludeListingId] uuid to filter out
 * @param {Array<string>} [options.excludeListingIds] more uuids to filter out
 * @param {number} [options.limit]
 * @returns {Array} listings
 */
export const pickCollectionListings = (listings = [], options = {}) => {
  const { excludeListingId, excludeListingIds = [], limit = DEFAULT_LIMIT } = options;
  const excluded = new Set([...excludeListingIds, excludeListingId].filter(Boolean));

  return listings
    .filter(listing => {
      const { deleted, state } = listing?.attributes || {};
      return !deleted && state !== 'closed' && !excluded.has(listing?.id?.uuid);
    })
    .slice(0, limit);
};

/**
 * Merge collection sections into the sections of a hosted page asset.
 *
 * If the operator has already placed a section with a matching sectionId in the Console,
 * the collection is rendered in that slot. Otherwise it is inserted just above the footer,
 * so the footer stays last.
 *
 * @param {Array} sections sections from the page asset
 * @param {Array} collectionSections sections to merge in
 * @returns {Array} sections
 */
export const injectCollectionSections = (sections = [], collectionSections = []) => {
  if (collectionSections.length === 0) {
    return sections;
  }

  const existingSectionIds = sections.map(section => section.sectionId);
  const placed = collectionSections.filter(s => existingSectionIds.includes(s.sectionId));
  const unplaced = collectionSections.filter(s => !existingSectionIds.includes(s.sectionId));

  const withReplacements = sections.map(
    section => placed.find(p => p.sectionId === section.sectionId) || section
  );

  const footerIndex = withReplacements.findIndex(section => section.sectionType === 'footer');
  const insertAt = footerIndex >= 0 ? footerIndex : withReplacements.length;

  return [
    ...withReplacements.slice(0, insertAt),
    ...unplaced,
    ...withReplacements.slice(insertAt),
  ];
};
